"use client"

import type { PCBStore } from "@/hooks/use-pcb-store"

interface StatusBarProps {
  store: PCBStore
}

const TOOL_LABELS: Record<string, string> = {
  select: "Select",
  polyline: "Polyline",
  polygon: "Polygon",
  rect: "Rectangle",
  circle: "Circle",
  freehand: "Freehand",
  text: "Text",
  marker: "Marker",
  probe: "Probe",
  measure: "Measure",
  eraser: "Eraser",
}

export function StatusBar({ store }: StatusBarProps) {
  const activeLayer = store.activeLayer

  return (
    <div className="flex items-center justify-between border-t border-border bg-toolbar-bg px-3 py-1">
      <div className="flex items-center gap-4">
        {/* Active tool */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground font-mono">Tool:</span>
          <span className="text-[10px] text-foreground font-mono">
            {TOOL_LABELS[store.activeTool] ?? store.activeTool}
          </span>
        </div>

        {/* Active layer */}
        {activeLayer && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground font-mono">Layer:</span>
            <div
              className="h-2 w-2 rounded-sm"
              style={{ backgroundColor: activeLayer.color }}
            />
            <span className="text-[10px] text-foreground font-mono">{activeLayer.name}</span>
            {activeLayer.locked && (
              <span className="text-[10px] text-destructive font-mono">(locked)</span>
            )}
            {!activeLayer.visible && (
              <span className="text-[10px] text-destructive font-mono">(hidden)</span>
            )}
          </div>
        )}

        {/* Stroke info */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground font-mono">Stroke:</span>
          <span className="text-[10px] text-foreground font-mono">{store.strokeWidth}px</span>
        </div>

        {/* Object count */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground font-mono">Objects:</span>
          <span className="text-[10px] text-foreground font-mono">
            {store.layers.reduce((acc, l) => acc + l.objects.length, 0)}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Zoom */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground font-mono">Zoom:</span>
          <span className="text-[10px] text-foreground font-mono">{(store.zoom * 100).toFixed(0)}%</span>
        </div>

        {/* Hint */}
        <span className="text-[10px] text-muted-foreground/60 font-mono">
          RightClick pan | Ctrl+Scroll zoom | Del remove | Shift snap 45deg | DblClick edit | Ctrl+Z/Y undo/redo
        </span>
      </div>
    </div>
  )
}
